import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { type ReactNode, useId, useLayoutEffect, useRef } from "react";
import { Box, ButtonBase, IconButton, Stack, Typography, styled, type SxProps, type Theme } from "~/common/mui";
import { ControlInfo } from "./ControlInfo";
import { ControlOptionLabel } from "./ControlOptionLabel";
import type { CarouselSelectControlViewModel, ControlOptionViewModel } from "./controlViewModel";

export interface CarouselSelectControlProps extends Omit<CarouselSelectControlViewModel, "type" | "controlIndex"> {
  onChange: (value: string) => void;
  synchronizedCardHeight?: number;
  onSelectedCardHeightChange?: (height: number) => void;
  children?: ReactNode;
  className?: string;
  sx?: SxProps<Theme>;
}

const Root = styled(Stack)({
  width: "100%",
  maxWidth: 416,
});

const Carousel = styled(Box)({
  position: "relative",
  width: "100%",
  maxWidth: 416,
  minWidth: 0,
  gap: 8,
  display: "flex",
  flexDirection: "column",
});

const PreviewArea = styled(Stack, {
  shouldForwardProp: (prop) => prop !== "showDescription",
})<{ showDescription: boolean }>(({ showDescription, theme }) => ({
  minHeight: theme.spacing(showDescription ? 16 : 14),
  boxSizing: "border-box",
  paddingTop: theme.spacing(2),
  alignItems: "center",
  justifyContent: "flex-end",
  isolation: "isolate",
}));

const BottomPreviewArea = styled(PreviewArea)(({ theme }) => ({
  justifyContent: "flex-start",
  paddingTop: 0,
  paddingBottom: theme.spacing(2),
}));

const PreviewCard = styled(ButtonBase, {
  shouldForwardProp: (prop) => prop !== "distance" && prop !== "isBottom" && prop !== "showDescription",
})<{ distance: 1 | 2; isBottom: boolean; showDescription: boolean }>(
  ({ distance, isBottom, showDescription, theme }) => ({
    width: distance === 1 ? `calc(100% - ${theme.spacing(6)})` : `calc(100% - ${theme.spacing(12)})`,
    minHeight: theme.spacing(7),
    padding: theme.spacing(1),
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    textAlign: "left",
    border: 0,
    borderRadius: theme.spacing(2),
    boxShadow: `inset 0 0 0 ${theme.spacing(0.125)} ${theme.palette.divider}`,
    backgroundColor: theme.palette.surfaceContainer.lowest,
    opacity: showDescription ? 1 : distance === 1 ? 0.8 : 0.6,
    boxSizing: "border-box",
    marginBottom: !showDescription && !isBottom && distance === 2 ? theme.spacing(-2) : undefined,
    marginTop: !showDescription && isBottom && distance === 2 ? theme.spacing(-2) : undefined,
    zIndex: distance === 1 ? 1 : undefined,
    "&:hover": {
      boxShadow: `inset 0 0 0 ${theme.spacing(0.125)} ${theme.palette.primary.main}`,
    },
  }),
);

const SelectedCard = styled(Box, {
  shouldForwardProp: (prop) => prop !== "hasError" && prop !== "selected" && prop !== "synchronizedHeight",
})<{ hasError: boolean; selected: boolean; synchronizedHeight?: number }>(
  ({ hasError, selected, synchronizedHeight, theme }) => ({
    position: "relative",
    zIndex: 1,
    width: "100%",
    minHeight: synchronizedHeight,
    padding: theme.spacing(1),
    borderRadius: theme.spacing(2),
    boxShadow: hasError
      ? `inset 0 0 0 ${theme.spacing(0.25)} ${theme.palette.error.main}`
      : selected
      ? `inset 0 0 0 ${theme.spacing(0.25)} ${theme.palette.primary.main}`
      : `inset 0 0 0 ${theme.spacing(0.125)} ${theme.palette.divider}`,
    backgroundColor: theme.palette.surfaceContainer.lowest,
    boxSizing: "border-box",
  }),
);

const SelectedCardContent = styled(Stack)(({ theme }) => ({
  gap: theme.spacing(1),
}));

const MainCardSelectButton = styled(ButtonBase)(({ theme }) => ({
  position: "absolute",
  inset: 0,
  zIndex: 2,
  borderRadius: theme.spacing(2),
  "&:hover": {
    boxShadow: `inset 0 0 0 ${theme.spacing(0.25)} ${theme.palette.primary.main}`,
  },
  "&:focus-visible": {
    outline: `${theme.spacing(0.25)} solid ${theme.palette.primary.main}`,
    outlineOffset: theme.spacing(0.25),
  },
}));

const ImageFrame = styled(Box)(({ theme }) => ({
  width: "100%",
  aspectRatio: "4 / 3",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  overflow: "hidden",
  backgroundColor: theme.palette.surfaceContainer.low,
}));

const OptionImage = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "contain",
});

const PlaceholderIcon = styled(ImageOutlinedIcon)(({ theme }) => ({
  color: theme.palette.text.secondary,
  width: "34%",
  height: "34%",
}));

const SelectionIcon = styled(CheckCircleRoundedIcon)(({ theme }) => ({
  position: "absolute",
  top: theme.spacing(0.75),
  right: theme.spacing(0.75),
  color: theme.palette.primary.main,
  backgroundColor: theme.palette.surfaceContainer.lowest,
  borderRadius: "50%",
  fontSize: theme.spacing(2.5),
}));

const NavigationButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  left: "50%",
  zIndex: 3,
  width: theme.spacing(5),
  height: theme.spacing(5),
  transform: "translateX(-50%)",
  color: theme.palette.primary.contrastText,
  backgroundColor: theme.palette.primary.main,
  "&:hover": {
    backgroundColor: theme.palette.primary.dark,
  },
  "&.Mui-disabled": {
    color: theme.palette.action.disabled,
    backgroundColor: theme.palette.action.disabledBackground,
  },
}));

const PreviousButton = styled(NavigationButton)(({ theme }) => ({
  top: theme.spacing(-0.5),
}));

const NextButton = styled(NavigationButton)(({ theme }) => ({
  bottom: theme.spacing(-0.5),
}));

const findSelectableOption = (options: readonly ControlOptionViewModel[], selectedIndex: number, direction: -1 | 1) => {
  for (let index = selectedIndex + direction; index >= 0 && index < options.length; index += direction) {
    if (!options[index].disabled) {
      return options[index];
    }
  }
  return undefined;
};

const selectedOptionDescriptionId = (prefix: string, option?: ControlOptionViewModel) =>
  option?.description ? `${prefix}-selected-${option.value}` : undefined;

const PreviewCards = ({
  options,
  bottom = false,
  disabled,
  descriptionIdPrefix,
  showDescription,
  onSelect,
}: {
  options: readonly ControlOptionViewModel[];
  bottom?: boolean;
  disabled: boolean;
  descriptionIdPrefix: string;
  showDescription: boolean;
  onSelect: (value: string) => void;
}) => {
  const Area = bottom ? BottomPreviewArea : PreviewArea;

  return (
    <Area showDescription={showDescription}>
      {options.map((option, index) => {
        const distance = (bottom ? index + 1 : options.length - index) as 1 | 2;
        const descriptionId = `${descriptionIdPrefix}-${option.value}`;
        return (
          <PreviewCard
            key={option.value}
            distance={distance}
            isBottom={bottom}
            showDescription={showDescription}
            role="radio"
            aria-checked={false}
            aria-label={option.label}
            aria-describedby={showDescription && option.description ? descriptionId : undefined}
            disabled={disabled || option.disabled}
            onClick={() => onSelect(option.value)}
          >
            <Stack gap={0.5} sx={{ width: "100%", minWidth: 0 }}>
              <Typography variant="bodyMedium" sx={{ fontWeight: 700 }}>
                {option.label}
              </Typography>
              {showDescription && option.description ? (
                <Typography id={descriptionId} variant="bodySmall" color="textSecondary" noWrap>
                  {option.description}
                </Typography>
              ) : null}
            </Stack>
          </PreviewCard>
        );
      })}
    </Area>
  );
};

export const CarouselSelectControl = ({
  label,
  hideLabel,
  info,
  helperText,
  error,
  errorText,
  disabled = false,
  value,
  options,
  previousLabel,
  nextLabel,
  standardLabel,
  showPreviewDescription,
  onChange,
  synchronizedCardHeight,
  onSelectedCardHeightChange,
  children,
  className,
  sx,
}: CarouselSelectControlProps) => {
  const previewDescriptionIdPrefix = useId();
  const selectedCardRef = useRef<HTMLDivElement>(null);
  const selectedCardContentRef = useRef<HTMLDivElement>(null);
  const selectedCardHeightChangeRef = useRef(onSelectedCardHeightChange);
  const shouldReportSelectedCardHeight = onSelectedCardHeightChange !== undefined;
  const assignedIndex = options.findIndex((option) => option.value === value);
  const hasSelection = assignedIndex >= 0;
  const selectedIndex = Math.max(assignedIndex, 0);
  const selectedOption = options[selectedIndex];
  const selectedDescriptionId = selectedOptionDescriptionId(previewDescriptionIdPrefix, selectedOption);
  const previousOption = hasSelection ? findSelectableOption(options, selectedIndex, -1) : undefined;
  const nextOption = hasSelection
    ? findSelectableOption(options, selectedIndex, 1)
    : options.find((option) => !option.disabled);
  const previousOptions = hasSelection ? options.slice(Math.max(0, selectedIndex - 2), selectedIndex) : [];
  const nextOptions = options.slice(selectedIndex + 1, selectedIndex + 3);

  useLayoutEffect(() => {
    selectedCardHeightChangeRef.current = onSelectedCardHeightChange;
  }, [onSelectedCardHeightChange]);

  useLayoutEffect(() => {
    const selectedCard = selectedCardRef.current;
    const content = selectedCardContentRef.current;
    if (!selectedCard || !content || !shouldReportSelectedCardHeight) {
      return;
    }

    const reportHeight = () => {
      const style = getComputedStyle(selectedCard);
      const height =
        content.getBoundingClientRect().height +
        Number.parseFloat(style.paddingTop) +
        Number.parseFloat(style.paddingBottom);
      if (Number.isFinite(height)) {
        selectedCardHeightChangeRef.current?.(height);
      }
    };

    reportHeight();
    const resizeObserver = new ResizeObserver(reportHeight);
    resizeObserver.observe(content);

    return () => resizeObserver.disconnect();
  }, [shouldReportSelectedCardHeight]);

  return (
    <Root className={className} sx={sx} gap={4} alignItems="center">
      {!hideLabel ? (
        <Stack direction="row" gap={1} alignItems="center" justifyContent="center">
          <Typography variant="bodyLarge" color={error ? "error" : "textPrimary"}>
            {label}
          </Typography>
          {info ? <ControlInfo {...info} /> : null}
        </Stack>
      ) : null}
      <Carousel role="radiogroup" aria-label={label}>
        <PreviewCards
          options={previousOptions}
          disabled={disabled}
          descriptionIdPrefix={previewDescriptionIdPrefix}
          showDescription={Boolean(showPreviewDescription)}
          onSelect={onChange}
        />
        <PreviousButton
          aria-label={previousLabel}
          disabled={disabled || !previousOption}
          onClick={() => previousOption && onChange(previousOption.value)}
        >
          <KeyboardArrowUpIcon />
        </PreviousButton>

        {selectedOption ? (
          <SelectedCard
            ref={selectedCardRef}
            role={hasSelection ? "radio" : undefined}
            aria-checked={hasSelection ? true : undefined}
            hasError={Boolean(error)}
            selected={hasSelection}
            synchronizedHeight={synchronizedCardHeight}
          >
            {!hasSelection ? (
              <MainCardSelectButton
                role="radio"
                aria-checked={false}
                aria-label={selectedOption.label}
                aria-describedby={selectedDescriptionId}
                disabled={disabled || selectedOption.disabled}
                onClick={() => onChange(selectedOption.value)}
              />
            ) : null}
            <SelectedCardContent ref={selectedCardContentRef}>
              <ImageFrame>
                {selectedOption.imageUrl ? <OptionImage src={selectedOption.imageUrl} alt="" /> : <PlaceholderIcon />}
              </ImageFrame>
              <Stack gap={0.5}>
                <ControlOptionLabel option={selectedOption} standardLabel={standardLabel} emphasis />
                {selectedOption.description ? (
                  <Typography id={selectedDescriptionId} variant="bodyMedium" color="textSecondary" noWrap>
                    {selectedOption.description}
                  </Typography>
                ) : null}
              </Stack>
              {children}
            </SelectedCardContent>
            {hasSelection ? <SelectionIcon aria-hidden /> : null}
          </SelectedCard>
        ) : null}

        <PreviewCards
          options={nextOptions}
          bottom
          disabled={disabled}
          descriptionIdPrefix={previewDescriptionIdPrefix}
          showDescription={Boolean(showPreviewDescription)}
          onSelect={onChange}
        />
        <NextButton
          aria-label={nextLabel}
          disabled={disabled || !nextOption}
          onClick={() => nextOption && onChange(nextOption.value)}
        >
          <KeyboardArrowDownIcon />
        </NextButton>
      </Carousel>
      {errorText ?? helperText ? (
        <Typography variant="bodySmall" color={error ? "error" : "textSecondary"}>
          {errorText ?? helperText}
        </Typography>
      ) : null}
    </Root>
  );
};
